import { LoginConsumer, LoginLoadingState, LoginProvider } from 'app/components/auth/LoginContext';
import RedirectToAuth from 'app/components/auth/RedirectToAuth';
import { T } from 'app/components/localization';
import RedirectingPage from 'app/components/pages/RedirectingPage';
import UnknownError from 'app/components/pages/UnknownError';
import { withHistory } from 'app/components/router/History';
import OauthDelegate from 'app/containers/auth/OauthDelegate';
import { revokeAuth as revokeUserAuth } from 'app/models/auth/actions';
import { isAuthenticatedSelector } from 'app/models/auth/selectors';
import { clearCookie } from 'app/services/cookies';
import { DMM_AUTH_COOKIE, DMM_COOKIE_DOMAIN } from 'app/services/cookies/constants';
import { History } from 'app/services/history';
import { IAuthenticateRequest } from 'app/services/oauth/AuthenticateRequest';
import { LoginIntent, LoginIntentType } from 'app/services/oauth/constants';
import { AppState } from 'app/store';
import { getWithDefault } from 'app/utils/fpTs';
import { applyMixins } from 'app/utils/mixins';
import { parseBoolean } from 'app/utils/parse';
import Cookies from 'js-cookie';
import qs from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { BrandContextValue, BrandConsumer } from '../OrganizationContext';
import { connectRenderProp } from 'app/utils/connect';

const DISPLAY_MODE_IFRAME = 'iframe';

interface IProps {
  query: {
    honoredIntent?: string;
    intent?: string;
    force_login?: string;
    display?: string;
  } & Partial<IAuthenticateRequest>;
  revokeAuth: () => Promise<void>;
  history: History;
  isAuthenticated: boolean;
  brand: BrandContextValue;
}

class OauthDelegatePage extends React.Component<IProps> {
  public async componentDidMount() {
    const { query, revokeAuth, history, isAuthenticated, brand } = this.props;
    const forceLogin = getWithDefault(parseBoolean(query.force_login || ''), false);
    // A query param that tells this component that the force_login option or register intent
    // has been already been honored so auth isn't revoked again for the newly logged in user.
    const honoredIntent = getWithDefault(parseBoolean(query.honoredIntent || ''), false);
    const intent = getWithDefault(LoginIntentType.decode(query.intent), LoginIntent.Login);

    if ((forceLogin || intent === LoginIntent.Register) && !honoredIntent) {
      if (Cookies.get(DMM_AUTH_COOKIE)) {
        clearCookie({
          context: {
            isFirstParty: brand.isFirstParty,
            isFirstPartyDmm: brand.firstPartyDmm,
            defaultDomain: brand.defaultDomain,
          },
          key: DMM_AUTH_COOKIE,
          options: { domain: DMM_COOKIE_DOMAIN },
        });
      }
      if (isAuthenticated) {
        await revokeAuth();
      }
      const newSearch = { ...qs.parse(history.location.search), honoredIntent: true };
      history.replace(history.extendHref({ search: qs.stringify(newSearch) }));
    }
  }

  public render() {
    // There are two main code paths for a login that runs through this
    // code path.
    //
    //   - the LoginProvider may trigger an automatic login
    //
    //     This will occur for all DMM users, since we unconditionally
    //     set `forceAutoLogin`, and rely on the external intent to be
    //     provided.  Note that this is a departure from the regular
    //     login pages on the site, which must check the backend to see
    //     if there's a matching account before deciding if it should
    //     auto login (existing eikaiwa users), or if it should present
    //     a login option (existing DMM users without eikaiwa
    //     accounts). See
    //     https://github.com/iknow/eikaiwa_content_frontend/issues/2058
    //     for motivation.
    //
    //   - otherwise the RedirectToAuth will show the regular login
    //   - page.
    //
    // When triggering the automatic login, all query parameters such as
    // `show_auth` and `dmm_env` are parsed by the LoginProvider.  In
    // the RedirectToAuth case, all of these variables must be
    // explicitly forwarded, or they will be dropped.
    //
    // Cleanup work: Using LoginProvider here should not be necessary,
    // and makes this a little hard to follow. Going via the Auth form
    // via RedirectToAuth, which itself handles automatic logins, should
    // be sufficient. The result is then there's only one code path for
    // logins.

    const { query, isAuthenticated } = this.props;
    const isIFrame      = query.display === DISPLAY_MODE_IFRAME;
    const intent        = getWithDefault(LoginIntentType.decode(query.intent), LoginIntent.Login);
    const forceLogin    = getWithDefault(parseBoolean(query.force_login || ''), false);
    const honoredIntent = getWithDefault(parseBoolean(query.honoredIntent || ''), false);

    // This flag is used to signal that delegation or forcing an auto dmm login shouldn't happen yet because auth
    // needs to be revoked first in order to properly honor the force_login option or the 'register' intent.
    const willRevokeCurrentAuth = (forceLogin || intent === LoginIntent.Register) && !honoredIntent;

    return willRevokeCurrentAuth ? <RedirectingPage /> :
    (
      <T>{t =>
        <LoginProvider forceAutoLogin intent={intent}>
          <LoginConsumer>{({ loadingState, showAllAuthMethods, dmmLoginEnv }) => {
            if (loadingState === LoginLoadingState.LOADING_INITIALIZING) {
              return null;
            } else if (loadingState === LoginLoadingState.LOADING_AUTO_LOGIN || willRevokeCurrentAuth) {
              return <RedirectingPage />;
            }

            if (isAuthenticated) {
              // Auto delegate, immediately.
              return (
                <OauthDelegate requestParams={query}>{({ error }) => {
                  if (error) {
                    return <UnknownError error={error} />;
                  }

                  return <RedirectingPage />;
                }}
                </OauthDelegate>
              );
            } else {
              // Only return to this component post auth if the query params required for delegation, response_type
              // and client_id, are present. Returning otherwise would result in a failed oauth login, so instead
              // use the normal post auth redirect logic by setting returnAfterAuth to false.
              return (
                <RedirectToAuth
                  intent={intent}
                  automaticLogin
                  iframe={isIFrame}
                  dmmLoginEnv={dmmLoginEnv}
                  showAllAuthMethods={showAllAuthMethods}
                  returnAfterAuth={Boolean(query.response_type && query.client_id)}
                />
              );
            }
          }}</LoginConsumer>
        </LoginProvider>
      }</T>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  isAuthenticated: isAuthenticatedSelector(state),
});

export const OauthDelegatePageUnconnected = OauthDelegatePage;
export default applyMixins(
  connectRenderProp('brand', BrandConsumer),
  withHistory,
  connect(mapStateToProps, { revokeAuth: revokeUserAuth }),
)(OauthDelegatePage);
